import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Users, Award, CheckCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff8af06a-976d-4efd-824f-28da774a6e41/artifacts/2h81g2hn_use%20pc%20hindi%20logo.png";

const HING_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ff8af06a-976d-4efd-824f-28da774a6e41/images/8d3d34df708a7f5de29bc09f393fd5b8a6f149ebadab7f4939aa86ad903833bc.png";

const AboutPage = () => {
  const values = [
    {
      icon: <Award className="w-8 h-8" />,
      title: "Quality First",
      description: "We never compromise on quality. Every product undergoes strict quality checks."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Customer Trust",
      description: "Building long-lasting relationships through transparency and reliability."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community Focus",
      description: "Supporting local farmers and contributing to community development."
    }
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-600 to-orange-800 text-white py-24" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-display text-4xl md:text-5xl font-bold mb-6">
                About Punjabi Chaska
              </h1>
              <p className="text-xl text-white/90 leading-relaxed">
                A journey of bringing authentic, premium quality Hing to Indian kitchens. 
                Our commitment to purity and tradition sets us apart.
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src={LOGO_URL} 
                alt="Punjabi Chaska Logo" 
                className="w-72 md:w-96 h-auto drop-shadow-2xl object-contain"
                data-testid="about-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="section-title mb-6">Our Story</h2>
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Punjabi Chaska was born from a simple yet powerful vision—to bring the 
                  authentic taste of pure, premium-quality Hing (Asafoetida) to every Indian kitchen. 
                  What started as a passion for quality spices has grown into a trusted brand 
                  that families rely on for their daily cooking needs.
                </p>
                <p>
                  Operating under the registered name Shri Vishnu Udhyog, 
                  we are a GST registered company committed to transparency, 
                  quality, and customer satisfaction.
                </p>
                <p>
                  Our Hing is carefully sourced and processed to preserve its natural aroma 
                  and flavor, ensuring that every dish you prepare is infused with authentic taste. 
                  We believe that great food starts with great ingredients, and that's exactly 
                  what we deliver.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src={HING_IMAGE} 
                alt="Indian Cooking" 
                className="rounded-xl shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-brand-orange text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">2026</div>
                <div className="text-sm">Est. Year</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-brand-cream" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-white p-10 rounded-xl shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-brand-orange mb-6">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To provide every Indian household with access to pure, premium-quality 
                Hing that enhances the flavor of traditional dishes while maintaining 
                the highest standards of hygiene and quality control.
              </p>
            </div>
            <div className="bg-white p-10 rounded-xl shadow-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-brand-orange mb-6">
                <Eye className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To become India's most trusted Hing brand, known for uncompromising 
                quality and authentic taste. We aim to be present in every kitchen 
                where flavor and purity matter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-white" data-testid="our-values">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-subtitle">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="text-center p-8 border border-gray-100 rounded-xl hover:shadow-lg transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-brand-orange mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-gray-900 text-white" data-testid="company-info">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-4xl font-bold mb-6">
                Registered & Compliant
              </h2>
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                Punjabi Chaska operates under Shri Vishnu Udhyog, 
                a GST registered company committed to transparency and quality.
              </p>
              <div className="bg-gray-800 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Company Name</p>
                    <p className="font-semibold">Shri Vishnu Udhyog</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">GSTIN</p>
                    <p className="font-mono text-orange-400 font-semibold">09EALPG7629N1ZW</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Location</p>
                    <p className="font-semibold">Bulandshahr, UP</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">State</p>
                    <p className="font-semibold">Uttar Pradesh</p>
                  </div>
                </div>
              </div>
              <Link to="/legal" className="btn-primary inline-block" data-testid="view-documents-btn">
                View Legal Documents
              </Link>
            </div>
            <div className="space-y-4">
              {[
                "Registered Company",
                "Quality Certified",
                "FSSAI Compliant",
                "Transparent Business Practices",
                "B2B Focused"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-gray-800 p-4 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-orange" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center text-white">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">
            Experience the Punjabi Chaska Difference
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of families who trust us for their kitchen essentials
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products" className="btn-outline" data-testid="explore-products-cta">
              Explore Products
            </Link>
            <Link to="/contact" className="btn-outline" data-testid="contact-us-cta">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
